// Collision resolution using spatial hash + swept circle tests.
// The arena is a torus: positions wrap at ±100 and all pairwise tests use the
// shortest signed delta across the seam. Conforms to GDD §14.4.

import type { Vec2, EntityBase } from '../types.js';
import { cfg } from '../config.js';

const ARENA_SIZE = cfg.getNumber('sim.arena_size');
const HALF_ARENA = ARENA_SIZE / 2;

export interface CollisionPair {
  a: EntityBase;
  b: EntityBase;
  normal: Vec2;
  penetration: number;
}

// Wrap a scalar into [-HALF_ARENA, HALF_ARENA). JS `%` keeps the dividend's
// sign, so negatives are shifted back into range before re-centering.
function wrapAxis(v: number): number {
  let r = (v + HALF_ARENA) % ARENA_SIZE;
  if (r < 0) r += ARENA_SIZE;
  return r - HALF_ARENA;
}

// Fast shortest signed delta across the seam for inputs already within one
// arena of each other. Branch-based; avoids modulo cost in hot loops.
function wrapDeltaAxis(d: number): number {
  if (d > HALF_ARENA) return d - ARENA_SIZE;
  if (d < -HALF_ARENA) return d + ARENA_SIZE;
  return d;
}

/**
 * Wrap a position onto the 200×200 torus: x = ((x + 100) % 200) - 100
 * (same for y), with negative modulo handled so any finite input lands in
 * [-100, 100). Pure arithmetic; deterministic.
 */
export function wrapArena(pos: Vec2): void {
  pos.x = wrapAxis(pos.x);
  pos.y = wrapAxis(pos.y);
}

/**
 * Shortest signed delta from `b` to `a` across the seam: each component is
 * wrapped into [-100, 100), so `b + wrappedDelta(a, b)` reaches `a` by the
 * shortest toroidal path.
 */
export function wrappedDelta(a: Vec2, b: Vec2): Vec2 {
  return { x: wrapDeltaAxis(a.x - b.x), y: wrapDeltaAxis(a.y - b.y) };
}

export function circleCircleSweep(
  a0: Vec2,
  a1: Vec2,
  ra: number,
  b0: Vec2,
  b1: Vec2,
  rb: number
): number {
  // Relative motion; solve quadratic for time of contact in [0,1].
  // Callers pass `b0`/`b1` unwrapped into `a`'s local frame (see wrappedDelta)
  // so the test is seamless across the torus boundary.
  const va = { x: a1.x - a0.x, y: a1.y - a0.y };
  const vb = { x: b1.x - b0.x, y: b1.y - b0.y };
  const v = { x: va.x - vb.x, y: va.y - vb.y };
  const p0 = { x: a0.x - b0.x, y: a0.y - b0.y };
  const r = ra + rb;
  const A = v.x * v.x + v.y * v.y;
  const B = 2 * (p0.x * v.x + p0.y * v.y);
  const C = p0.x * p0.x + p0.y * p0.y - r * r;

  if (A < 1e-8) return C <= 0 ? 0 : -1;
  const disc = B * B - 4 * A * C;
  if (disc < 0) return -1;
  const sqrt = Math.sqrt(disc);
  const t1 = (-B - sqrt) / (2 * A);
  const t2 = (-B + sqrt) / (2 * A);
  if (t2 < 0 || t1 > 1) return -1;
  return Math.max(0, t1);
}

export function staticCircleOverlap(a: Vec2, ra: number, b: Vec2, rb: number): boolean {
  const d = wrappedDelta(a, b);
  const r = ra + rb;
  return d.x * d.x + d.y * d.y < r * r;
}

export function resolveStaticSeparation(a: EntityBase, b: EntityBase, minSep: number): void {
  const d = wrappedDelta(a.pos, b.pos);
  const distSq = d.x * d.x + d.y * d.y;
  const target = minSep;
  if (distSq >= target * target || distSq < 1e-8) return;
  const dist = Math.sqrt(distSq);
  const nx = d.x / dist;
  const ny = d.y / dist;
  const push = (target - dist) * 0.5;
  a.pos.x += nx * push;
  a.pos.y += ny * push;
  b.pos.x -= nx * push;
  b.pos.y -= ny * push;
}
